import {
  AfterViewInit,
  ContentChildren,
  Directive,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChange
} from '@angular/core';
import {merge} from 'rxjs';

import {SearchQuery} from '../../fiql/fiql.service';

import {TableColumnFilter} from './table-filter.component';
import {FilterConfig, FilterState} from './table.interfaces';

@Directive({
  selector: '[gp-table-filters]'
})
export class TableColumnFilters implements AfterViewInit, OnChanges {

  @HostBinding('class.hide')
  get hidden() {
    return !this.open && this.filters.filter(filter => filter.value !== undefined).length === 0;
  }

  @Input('default-state')
  public defaultState: string;

  @Input('gp-table-filters')
  public searchQuery: SearchQuery;

  public open: boolean = false;

  @ContentChildren(TableColumnFilter)
  private filters: QueryList<TableColumnFilter>;

  @Output('gp-table-filtersChange')
  private searchQueryChange: EventEmitter<SearchQuery> = new EventEmitter<SearchQuery>();

  private inSync: boolean = false;

  public ngOnChanges(changes: { [property: string]: SimpleChange }) {
    if (this.filters === undefined || this.searchQuery === undefined) {
      return;
    }
    this.syncFilters();
  }

  public ngAfterViewInit() {
    // Listen to filter changes on all filter columns of this table
    merge(...this.filters.map(filter => filter.filterChanged)).subscribe(this.filterChanged.bind(this));
  }

  public filterChanged(state: FilterState) {
    if (this.inSync) {
      return;
    }
    // First remove possible existing filter with that name
    const existing = this.findExistingFilter(state.config);
    if (existing) {
      this.searchQuery.splice(this.searchQuery.indexOf(existing), 1);
    }

    if (state.value !== '') {
      // Then add the new one if its value is not empty
      if (!Array.isArray(state.config)) {
        this.searchQuery.push({
          name: (<FilterConfig>state.config).key,
          value: (<FilterConfig>state.config).partialMatch ? `*${state.value}*` : state.value,
          operator: '=='
        });
      } else {
        this.searchQuery.push({
          name: (<Array<FilterConfig>>state.config).map(c => c.key).sort().join('|'),
          value: (<Array<FilterConfig>>state.config)
            .filter(config => {
              if (config.type !== undefined) {
                if (config.type === 'integer') {
                  return !isNaN(parseInt(state.value, 10));
                }
                if (config.type === 'string') {
                  return isNaN(parseInt(state.value, 10));
                }
              }
              return true;
            })
            .map(config => {
              return {
                name: config.key,
                value: config.partialMatch ? `*${state.value}*` : state.value,
                operator: '=='
              };
            }),
          operator: ','
        });
      }
    }

    this.searchQueryChange.emit(this.searchQuery);
  }

  private syncFilters() {
    this.inSync = true;
    this.filters.forEach(filter => {
      const found = this.findExistingFilter(filter.config);

      if (found && typeof found.value === 'string') {
        filter.value = <string>found.value;
      } else if (found && Array.isArray(found.value) && (<Array<any>>found.value).length > 0) {
        const expression = (<Array<any>>found.value).find(expr => !expr.value.startsWith('*'));
        if (expression) {
          filter.value = expression.value;
        }
      }
    });
    setTimeout(() => this.inSync = false);
    this.searchQueryChange.emit(this.searchQuery);
  }

  private findExistingFilter(config: FilterConfig | Array<FilterConfig>) {
    const filterKeys = (Array.isArray(config) ?
      (<Array<FilterConfig>>config).map(f => f.key) :
      [(<FilterConfig>config).key]);

    return this.searchQuery.find(expression => {
      const parts = expression.name.split('|');
      return !!filterKeys.filter(key => parts.indexOf(key) !== -1).length;
    });
  }
}
